import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import { Field } from "@/components/Form";
import { Spinner } from "@/components/Loader";
import { useAuth } from "@/lib/auth";
import { apiPut, apiPost, apiDelete, apiGet, setTokens } from "@/lib/api";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";
import { fmtMoney, formatPhoneUS, onlyDigits, phoneToE164, titleCase } from "@/lib/format";

// Hoisted to module scope — defining this inside Me() caused the input
// subtree to unmount/remount on every keystroke, which broke iOS AutoFill
// (single char inserted, keyboard dismissed, field clearing on retry).
function Section({ title, testId, children }) {
  return (
    <div data-testid={testId} style={{ marginTop: 24, background: C.pure, border: `1.5px solid ${C.warmGray3}` }}>
      <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.warmGray4}`, fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>
        {title}
      </div>
      <div style={{ padding: 22 }}>{children}</div>
    </div>
  );
}

// Affiliate card with five visual states, driven by the
// `consumer.affiliate` snapshot the BE attaches to /api/consumer/profile.
// Keeping all states in one component (instead of one card per state)
// avoids layout shift between renders.
function AffiliateCard({ affiliate, consumerEmail }) {
  // No affiliate record on file → keep showing the cross-promo CTA so
  // members with audiences can discover the program.
  if (!affiliate || !affiliate.status) {
    return (
      <a
        href="/affiliates"
        data-testid="me-affiliate-link"
        data-affiliate-state="none"
        style={{ textDecoration: "none", display: "block", marginTop: 16 }}
      >
        <div style={cardOuter()}>
          <div style={{ minWidth: 0 }}>
            <div style={cardEyebrow()}>★ INTERESTED IN OUR AFFILIATE PROGRAM?</div>
            <div style={cardBody()}>
              If you have a wellness audience, our affiliate program offers cash commission on top of your refer-a-friend credits. $40 upfront + 15% recurring for 12 months.
            </div>
            <div style={cardCTA()}>LEARN MORE →</div>
          </div>
          <span aria-hidden style={cardArrow()}>↗</span>
        </div>
      </a>
    );
  }

  // Hard-fail states — we don't surface the card at all so the member
  // isn't reminded their application went sideways every time they
  // open ME. Ops can still see status via the admin tools.
  if (["declined", "suspended", "terminated"].includes(affiliate.status)) {
    return null;
  }

  // Pending — application received, sitting with ops.
  if (affiliate.status === "pending") {
    return (
      <div
        data-testid="me-affiliate-link"
        data-affiliate-state="pending"
        style={{ marginTop: 16 }}
      >
        <div style={cardOuter()}>
          <div style={{ minWidth: 0 }}>
            <div style={cardEyebrow()}>★ APPLICATION RECEIVED — UNDER REVIEW</div>
            <div style={cardBody()}>
              Thanks for applying to the VOYA9 affiliate program. We typically respond within 5 business days. You'll get an email at <strong style={{ color: C.cream }}>{consumerEmail}</strong> with the next steps.
            </div>
          </div>
          <span aria-hidden style={{ fontSize: 22, color: C.peach, flexShrink: 0 }}>⏳</span>
        </div>
      </div>
    );
  }

  // Approved — two flavours depending on whether the member has
  // already completed the setup flow.
  if (affiliate.status === "approved") {
    if (affiliate.dashboardReady) {
      // Active affiliate — send them straight to the dashboard.
      return (
        <a
          href="/affiliates/dashboard"
          data-testid="me-affiliate-link"
          data-affiliate-state="active"
          style={{ textDecoration: "none", display: "block", marginTop: 16 }}
        >
          <div style={cardOuter({ accent: true })}>
            <div style={{ minWidth: 0 }}>
              <div style={cardEyebrow({ light: true })}>★ YOU'RE A VOYA9 AFFILIATE</div>
              <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.55, color: "rgba(250,246,240,0.85)" }}>
                Track conversions, grab assets, and review payouts. Your code{" "}
                <strong style={{ color: C.peach, fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.05em" }}>
                  {affiliate.code}
                </strong>
                {affiliate.tier ? ` · ${affiliate.tier} tier` : ""}.
              </div>
              <div style={cardCTA()}>GO TO AFFILIATE DASHBOARD →</div>
            </div>
            <span aria-hidden style={cardArrow()}>↗</span>
          </div>
        </a>
      );
    }
    // Approved but the member hasn't completed setup yet — usually means
    // they haven't clicked the one-time setup link in the email.
    return (
      <div
        data-testid="me-affiliate-link"
        data-affiliate-state="approved_setup_pending"
        style={{ marginTop: 16 }}
      >
        <div style={cardOuter({ accent: true })}>
          <div style={{ minWidth: 0 }}>
            <div style={cardEyebrow({ light: true })}>★ APPROVED — CHECK YOUR EMAIL</div>
            <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.55, color: "rgba(250,246,240,0.85)" }}>
              Congrats — your affiliate application is approved. We sent a one-time setup link to{" "}
              <strong style={{ color: C.cream }}>{consumerEmail}</strong> to finish password + payout setup.
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: "rgba(250,246,240,0.55)", fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
              Didn't get it? Check spam, or contact support and we'll resend.
            </div>
          </div>
          <span aria-hidden style={{ fontSize: 22, color: C.peach, flexShrink: 0 }}>✉</span>
        </div>
      </div>
    );
  }

  // Unknown status — render nothing rather than risk a misleading state.
  return null;
}

function cardOuter({ accent = false } = {}) {
  return {
    background: C.ink,
    color: C.cream,
    padding: 22,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    border: `1px solid ${accent ? C.peach : C.warmGray}`,
  };
}
function cardEyebrow({ light = false } = {}) {
  return { fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: light ? C.peach : C.peach };
}
function cardBody() {
  return { marginTop: 8, fontSize: 14, lineHeight: 1.55, color: "rgba(250,246,240,0.78)" };
}
function cardCTA() {
  return { marginTop: 10, fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peach };
}
function cardArrow() {
  return { fontSize: 22, color: C.peach, flexShrink: 0 };
}

// Inline email-change flow. Also hoisted to module scope to prevent remounts.
// Posts to /api/consumer/profile/change-email which requires current password,
// rotates the session tokens on success and returns the updated consumer.
// Per backend contract: direct change (no verification round-trip).
function EmailChangeForm({ currentEmail, onClose, onChanged }) {
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast("Enter a valid email address.", "error");
      return;
    }
    if (newEmail.trim().toLowerCase() === (currentEmail || "").trim().toLowerCase()) {
      toast("That's already your current email.", "error");
      return;
    }
    if (!currentPassword) {
      toast("Please confirm your current password.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiPost("/consumer/profile/change-email", {
        newEmail: newEmail.trim().toLowerCase(),
        currentPassword,
      });
      // Rotate tokens so the session binds to the new email.
      if (res?.token || res?.refreshToken) {
        setTokens(res.token, res.refreshToken);
      }
      toast(`Email updated to ${res?.email || newEmail.trim().toLowerCase()}.`, "success");
      onClose();
      await onChanged?.();
    } catch (err) {
      if (err.status === 401 || err.status === 403) {
        toast("Current password is incorrect.", "error");
      } else if (err.status === 409) {
        toast("That email is already in use on another account.", "error");
      } else if (err.status === 400) {
        toast("That's already your current email.", "error");
      } else if (err.status === 422) {
        toast("That email address isn't valid.", "error");
      } else if (err.status === 404) {
        toast("Email changes aren't available just yet — please contact support@voya9.com.", "info");
      } else {
        toast(err.detail || "Could not update email. Try again in a moment.", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form data-testid="email-change-form" onSubmit={submit} style={{ marginTop: 18, padding: 18, background: C.bone, borderLeft: `3px solid ${C.peachDeep}` }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDeep, marginBottom: 8 }}>
        ★ CHANGE EMAIL
      </div>
      <p style={{ margin: "0 0 14px", fontSize: 13, lineHeight: 1.55, color: C.warmGray }}>
        Confirm your current password to change the email on this account.
        We'll email the old address to let you know the change happened.
      </p>
      <Field
        label="NEW EMAIL"
        type="email"
        value={newEmail}
        onChange={setNewEmail}
        placeholder="you@newdomain.com"
        testId="email-change-new"
        autoFocus
      />
      <Field
        label="CONFIRM CURRENT PASSWORD"
        type="password"
        value={currentPassword}
        onChange={setCurrentPassword}
        placeholder="••••••••"
        testId="email-change-password"
      />
      <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Button variant="ghost" testId="email-change-cancel" onClick={onClose}>CANCEL</Button>
        <Button variant="primary" testId="email-change-submit" type="submit" disabled={submitting}>
          {submitting ? "UPDATING…" : "UPDATE EMAIL →"}
        </Button>
      </div>
    </form>
  );
}

export default function Me() {
  const { consumer, refreshProfile, logout } = useAuth();
  const [editing, setEditing] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);
  const [form, setForm] = useState(null);
  const [credits, setCredits] = useState(null);
  const [pms, setPms] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (consumer) {
      setForm({
        firstName: consumer.firstName || "",
        lastName: consumer.lastName || "",
        phone: consumer.phone || "",
        line1: consumer.shippingAddress?.line1 || "",
        line2: consumer.shippingAddress?.line2 || "",
        city: consumer.shippingAddress?.city || "",
        state: consumer.shippingAddress?.state || "",
        zip: consumer.shippingAddress?.zip || "",
      });
    }
    apiGet("/consumer/credits").then(setCredits).catch(() => setCredits({ balance: 0 }));
    apiGet("/consumer/stripe/payment-methods").then(setPms).catch(() => setPms([]));
  }, [consumer]);

  if (!consumer || !form) return <div style={{ padding: 64, textAlign: "center" }}><Spinner /></div>;

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await apiPut("/consumer/profile", {
        firstName: titleCase(form.firstName),
        lastName: titleCase(form.lastName),
        phone: form.phone ? phoneToE164(form.phone) : "",
        shippingAddress: { line1: form.line1, line2: form.line2, city: form.city, state: form.state, zip: form.zip },
      });
      await refreshProfile();
      setEditing(false);
      toast("Profile saved.", "success");
    } catch (err) {
      toast(err.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const delAccount = async () => {
    if (!window.confirm("Delete your account? This is reversible for 30 days.")) return;
    try {
      await apiDelete("/consumer/account");
      toast("Account scheduled for deletion.", "success");
      logout();
    } catch (err) {
      toast(err.detail || "Could not delete", "error");
    }
  };

  return (
    <div data-testid="me-page" style={{ background: C.cream, paddingBottom: 80 }}>
      <section
        style={{
          background: C.ink,
          color: C.cream,
          padding: "48px 24px 88px",
          // Section is clipped directly so the diagonal IS the bottom
          // edge — no overlay triangle / next-section TopWedge to leak
          // a flat sliver between the colors.
          clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 calc(100% - 32px))",
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <Eyebrow color={C.peach}>★ ACCOUNT</Eyebrow>
          <h1 style={{ marginTop: 18, fontSize: "clamp(40px, 7vw, 72px)", fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 0.95, textTransform: "uppercase" }}>
            HELLO,
            <br />
            <span style={{ fontFamily: '"Instrument Serif", serif', fontStyle: "italic", fontWeight: 400, textTransform: "none", color: C.peach }}>
              {consumer.firstName || "friend"}.
            </span>
          </h1>
        </div>
      </section>

      <section style={{ padding: "32px 24px 52px", position: "relative", background: C.cream }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>

          <Link to="/app/refer" style={{ textDecoration: "none" }} data-testid="me-refer-link">
            <div style={{ background: C.peach, color: C.peachDark, padding: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em" }}>★ REFER & EARN</div>
                <div style={{ marginTop: 6, fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>
                  {fmtMoney((credits && credits.balance) || 0)} credit
                </div>
              </div>
              <span style={{ fontSize: 22 }}>→</span>
            </div>
          </Link>

          {/* Affiliate card — dynamic based on the `consumer.affiliate`
              snapshot the BE attaches to /api/consumer/profile (Option
              A per BE coordination, May 2026). Five visual states:
              - no record       → marketing CTA (cross-promo)
              - pending         → "Under review" reassurance
              - approved + ready → "Go to dashboard" link
              - approved + !ready → "Check your email for setup link"
              - declined/suspended/terminated → omit entirely */}
          <AffiliateCard affiliate={consumer.affiliate} consumerEmail={consumer.email} />

          <Section title="PROFILE" testId="profile-section">
            {!editing ? (
              <div>
                <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                  <div><strong>{titleCase(consumer.firstName)} {titleCase(consumer.lastName)}</strong></div>
                  <div>{consumer.email}</div>
                  {consumer.phone && <div>{formatPhoneUS(consumer.phone) || consumer.phone}</div>}
                  {consumer.shippingAddress?.line1 && (
                    <div style={{ marginTop: 10, color: C.warmGray }}>
                      {consumer.shippingAddress.line1}{consumer.shippingAddress.line2 ? `, ${consumer.shippingAddress.line2}` : ""}<br />
                      {consumer.shippingAddress.city}, {consumer.shippingAddress.state} {consumer.shippingAddress.zip}
                    </div>
                  )}
                </div>
                <div style={{ marginTop: 16 }}>
                  <Button variant="ghost" testId="profile-edit-btn" onClick={() => setEditing(true)}>EDIT PROFILE</Button>
                </div>
              </div>
            ) : (
              <form onSubmit={save}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <Field label="FIRST NAME" value={form.firstName} onChange={(v) => setForm((p) => ({ ...p, firstName: v }))} onBlur={() => setForm((p) => ({ ...p, firstName: titleCase(p.firstName) }))} testId="profile-firstname" />
                  <Field label="LAST NAME" value={form.lastName} onChange={(v) => setForm((p) => ({ ...p, lastName: v }))} onBlur={() => setForm((p) => ({ ...p, lastName: titleCase(p.lastName) }))} testId="profile-lastname" />
                </div>

                {/* Email — verify-first change flow. We show the current email
                    with a CHANGE button; tapping opens an inline panel that
                    requests a verification email. Email doesn't actually change
                    until the user clicks the link in their new inbox. */}
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 8 }}>
                    EMAIL
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "12px 14px",
                      background: C.bone,
                      border: `1.5px solid ${C.warmGray3}`,
                      flexWrap: "wrap",
                    }}
                  >
                    <span data-testid="profile-email-display" style={{ flex: 1, minWidth: 0, fontSize: 14, color: C.ink, overflowWrap: "anywhere" }}>
                      {consumer.email}
                    </span>
                    {!changingEmail && (
                      <button
                        type="button"
                        data-testid="profile-email-change-btn"
                        onClick={() => setChangingEmail(true)}
                        style={{
                          padding: "6px 12px",
                          fontSize: 11,
                          fontWeight: 800,
                          letterSpacing: "0.18em",
                          color: C.ink,
                          background: "transparent",
                          border: `1.5px solid ${C.ink}`,
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        CHANGE
                      </button>
                    )}
                  </div>
                  {changingEmail && (
                    <EmailChangeForm
                      currentEmail={consumer.email}
                      onClose={() => setChangingEmail(false)}
                      onChanged={refreshProfile}
                    />
                  )}
                </div>

                <Field label="PHONE" value={formatPhoneUS(form.phone)} onChange={(v) => setForm((p) => ({ ...p, phone: onlyDigits(v) }))} testId="profile-phone" />
                <Field label="ADDRESS LINE 1" value={form.line1} onChange={(v) => setForm((p) => ({ ...p, line1: v }))} testId="profile-line1" />
                <Field label="ADDRESS LINE 2" value={form.line2} onChange={(v) => setForm((p) => ({ ...p, line2: v }))} testId="profile-line2" />
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10 }}>
                  <Field label="CITY" value={form.city} onChange={(v) => setForm((p) => ({ ...p, city: v }))} testId="profile-city" />
                  <Field label="STATE" value={form.state} onChange={(v) => setForm((p) => ({ ...p, state: v.toUpperCase() }))} testId="profile-state" />
                  <Field label="ZIP" value={form.zip} onChange={(v) => setForm((p) => ({ ...p, zip: v }))} testId="profile-zip" />
                </div>
                <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
                  <Button variant="ghost" testId="profile-cancel-btn" onClick={() => setEditing(false)}>CANCEL</Button>
                  <Button variant="primary" testId="profile-save-btn" type="submit" disabled={saving} block>{saving ? "SAVING…" : "SAVE PROFILE →"}</Button>
                </div>
              </form>
            )}
          </Section>

          <Section title="BILLING" testId="billing-section">
            {(pms || []).length === 0 ? (
              <div style={{ fontSize: 14, color: C.warmGray }}>No payment methods on file.</div>
            ) : (
              (pms || []).map((p) => (
                <div key={p.id} data-testid={`pm-row-${p.last4}`} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px dashed ${C.warmGray4}`, fontSize: 14 }}>
                  <span>💳 {(p.brand || "").toUpperCase()} ending {p.last4}</span>
                  <span style={{ color: C.warmGray, fontSize: 12 }}>{p.expMonth?.toString().padStart(2, "0")}/{String(p.expYear).slice(-2)} {p.isDefault && <strong style={{ color: C.peachDeep }}>· DEFAULT</strong>}</span>
                </div>
              ))
            )}
            <div style={{ marginTop: 14 }}>
              <Link to="/app/plan/orders" data-testid="billing-orders-link" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.ink, textDecoration: "underline" }}>VIEW ORDER HISTORY →</Link>
            </div>
          </Section>

          <Section title="INJECTION TOOLKIT" testId="injection-toolkit-section">
            <div style={{ fontSize: 13, color: C.warmGray, lineHeight: 1.6, marginBottom: 14 }}>
              Your protocol card, reference library, quick-log, and full injection history — all in one place.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 14 }}>
              <Link
                to="/app/reference/injections"
                data-testid="me-injection-reference-link"
                style={{ color: C.ink, textDecoration: "underline" }}
              >
                Protocol card &amp; foundations →
              </Link>
              <Link
                to="/app/injection/log"
                data-testid="me-injection-log-link"
                style={{ color: C.ink, textDecoration: "underline" }}
              >
                Log an injection →
              </Link>
              <Link
                to="/app/injection/history"
                data-testid="me-injection-history-link"
                style={{ color: C.ink, textDecoration: "underline" }}
              >
                View injection history →
              </Link>
              <Link
                to="/app/onboarding/injection"
                data-testid="me-injection-onboarding-link"
                style={{ color: C.ink, textDecoration: "underline" }}
              >
                Revisit injection onboarding →
              </Link>
            </div>
          </Section>

          <Section title="LEGAL" testId="legal-section">
            <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 14 }}>
              <Link to="/legal/terms" data-testid="legal-terms-link" style={{ color: C.ink, textDecoration: "underline" }}>Terms of Service</Link>
              <Link to="/legal/privacy" data-testid="legal-privacy-link" style={{ color: C.ink, textDecoration: "underline" }}>Privacy Policy</Link>
              <Link to="/legal/telehealth" data-testid="legal-telehealth-link" style={{ color: C.ink, textDecoration: "underline" }}>Telehealth Informed Consent</Link>
            </div>
          </Section>

          <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 14 }}>
            <Button variant="ghost" testId="me-signout-btn" onClick={logout}>SIGN OUT</Button>
            <button data-testid="me-delete-btn" onClick={delAccount} style={{ fontFamily: '"Instrument Serif", serif', fontStyle: "italic", color: C.coral, fontSize: 16, textDecoration: "underline" }}>
              Delete my account
            </button>
            <div style={{ fontSize: 11, color: C.warmGray, textAlign: "center", marginTop: 20, letterSpacing: "0.05em" }}>
              VOYA9 — v1.0 · {consumer.email}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
